/**
 * Re-export of the canonical state-machine derivation from the parent repo
 * (`src/lib/ui-state.ts`). The Directus adapter writes `ui_state` into the
 * row whenever `status` or `generation_phases` change, so the SPA almost
 * never needs to call `deriveUiState` itself — it just reads `ui_state`
 * straight from the piece.
 *
 * `deriveUiState` stays exported here so we have a fallback for older rows
 * that haven't been touched since the migration in case `ui_state` is null,
 * and so the kanban can still group pieces when a write is in flight.
 */
export {
  deriveUiState,
  nextStepFor,
  shouldRecomputeUiState,
} from "@shared/ui-state";

export type {
  ArticleStatus as PieceStatus,
  GenerationPhase,
  UiState,
} from "@shared/ui-state";

import type { UiState } from "@shared/ui-state";

/**
 * Human-readable label per `ui_state`. Used in pills and column headers.
 */
export function uiStateLabel(state: UiState | string | null | undefined): string {
  switch (state) {
    case "idea":
      return "Idea";
    case "ai_researching":
      return "AI · Researching";
    case "ai_outlining":
      return "AI · Outlining";
    case "ai_drafting":
      return "AI · Drafting";
    case "awaiting_review":
      return "Awaiting review";
    case "ai_factchecking":
      return "AI · Fact-checking";
    case "ai_editing":
      return "AI · Editing";
    case "ai_optimizing":
      return "AI · SEO";
    case "ai_imaging":
      return "AI · Imaging";
    case "awaiting_approval":
      return "Awaiting approval";
    case "ai_publishing":
      return "AI · Publishing";
    case "published":
      return "Published";
    case "scheduled":
      return "Scheduled";
    case "rejected":
      return "Discarded";
    case "failed":
      return "Failed";
    case "revision_requested":
      return "Revision requested";
    case "ai_worldbuilding":
      return "AI · Worldbuilding";
    case "ai_architecting":
      return "AI · Architecting";
    case "ai_drafting_fiction":
      return "AI · Drafting (fiction)";
    case "ai_continuity_checking":
      return "AI · Continuity";
    case "ai_proseEditing":
      return "AI · Prose editing";
    case "ai_illustrating":
      return "AI · Illustrating";
    case "fiction_published":
      return "Published (fiction)";
    default:
      return state || "Unknown";
  }
}

/**
 * Kanban column membership. The five columns the SPA shows. Pieces whose
 * state isn't in any column get filtered out.
 */
export type KanbanColumn =
  | "ideas"
  | "ai_working"
  | "awaiting_human"
  | "scheduled"
  | "published";

const COLUMN_FOR: Record<string, KanbanColumn> = {
  idea: "ideas",
  ai_researching: "ai_working",
  ai_outlining: "ai_working",
  ai_drafting: "ai_working",
  ai_factchecking: "ai_working",
  ai_editing: "ai_working",
  ai_optimizing: "ai_working",
  ai_imaging: "ai_working",
  ai_publishing: "scheduled",
  ai_worldbuilding: "ai_working",
  ai_architecting: "ai_working",
  ai_drafting_fiction: "ai_working",
  ai_continuity_checking: "ai_working",
  ai_proseEditing: "ai_working",
  ai_illustrating: "ai_working",
  awaiting_review: "awaiting_human",
  awaiting_approval: "awaiting_human",
  // BUG-081 — a requested revision is work the pipeline owes back, not work
  // waiting on a person. queue-groups.ts already took that position (its own
  // comment reads "revision_requested (back with the pipeline)") and dropped
  // these from the queue; this map disagreed and left them in Your Turn. The
  // founder asked for a revision, watched the pill change and the card stay
  // put, and read that as the click doing nothing.
  revision_requested: "ai_working",
  // BUG-036 — a piece whose every publish target failed (publisher.ts's
  // "all targets failed" branch) previously had nowhere to render: no
  // column claimed `failed`, so it silently vanished from the board even
  // though nothing was deleted. Surfaced in Awaiting Approval — it needs a
  // human to fix credentials/config and retry, same as a review-pending piece.
  failed: "awaiting_human",
  scheduled: "scheduled",
  published: "published",
  fiction_published: "published",
};

export function kanbanColumnFor(
  state: UiState | string | null | undefined
): KanbanColumn | null {
  if (!state) return null;
  return COLUMN_FOR[state] ?? null;
}

/**
 * Piece-level column resolver. Same as `kanbanColumnFor` but knows about
 * a few status/phase tuples that don't map cleanly to a single ui_state:
 *
 *   - `proposal` + any phase set: Scout has run; the orchestrator pauses
 *     here waiting for the user to flip to `selected`. The `ui_state` says
 *     `ai_outlining`, but semantically this row is human-gated, not AI-gated.
 *     Surface it in Awaiting Approval so reviewers can act on it.
 */
export function kanbanColumnForPiece(piece: {
  ui_state?: string | null;
  status?: string | null;
  generation_phases?: string | null;
  run_phase?: string | null;
}): KanbanColumn | null {
  if (piece.status === "proposal" && piece.generation_phases) {
    return "awaiting_human";
  }
  // FEAT-089 — a queued idea has left the user's hands. `ui_state` cannot say
  // so: it is derived from (status, generation_phases) and neither of those
  // moves when a run is requested. `run_phase` is the axis that does, and
  // POST /run writes "queued" to it on the way in — for a pg-boss job and, now,
  // for a manual workspace waiting on the operator's sweep. Without this the
  // card sits in Ideas looking untouched until a skill writes its first phase,
  // which is minutes later and reads as a dead button.
  if (isQueued(piece)) return "ai_working";
  return kanbanColumnFor(piece.ui_state);
}

/** A run has been asked for and nothing has started it yet. */
export function isQueued(piece: {
  ui_state?: string | null;
  run_phase?: string | null;
}): boolean {
  return piece.run_phase === "queued" && kanbanColumnFor(piece.ui_state) === "ideas";
}

/**
 * The label for a piece's state, as opposed to `uiStateLabel`'s label for a
 * `ui_state` value. They differ only while a piece is queued, which is a fact
 * about `run_phase` that `ui_state` has no value for.
 */
export function pieceStateLabel(piece: {
  ui_state?: string | null;
  run_phase?: string | null;
}): string {
  return isQueued(piece) ? "Queued" : uiStateLabel(piece.ui_state);
}

/**
 * FEAT-038 — can this piece be stopped/reset? True when the pipeline is
 * actively working, or a run is wedged (a run_phase is set and not finished) —
 * and NOT when the piece is at a human gate or terminal (nothing to stop there).
 * Drives the admin-only Stop button.
 */
export function isStoppable(piece: {
  ui_state?: string | null;
  run_phase?: string | null;
}): boolean {
  const col = kanbanColumnFor(piece.ui_state);
  if (col === "awaiting_human" || col === "scheduled" || col === "published") {
    return false;
  }
  if (col === "ai_working") return true;
  // Wedged run: a run_phase is set and not in a clean finished state.
  const rp = piece.run_phase ?? null;
  return rp !== null && rp !== "done";
}

export interface ColumnDef {
  id: KanbanColumn;
  title: string;
  sub: string;
  tone: "neutral" | "yellow" | "blue" | "green" | "muted";
}

export const KANBAN_COLUMNS: readonly ColumnDef[] = [
  { id: "ideas", title: "Ideas", sub: "Not yet started", tone: "muted" },
  { id: "ai_working", title: "In Progress", sub: "Pipeline running", tone: "yellow" },
  {
    id: "awaiting_human",
    // FEAT-087 — renamed from "Awaiting Approval". The column holds
    // awaiting review, awaiting approval AND publish failures, so naming it
    // after one of its members was both redundant with the card's own pill
    // and wrong for the others. The approved design calls it Your Turn.
    title: "Your Turn",
    sub: "Ready for review",
    tone: "blue",
  },
  { id: "scheduled", title: "Scheduled", sub: "Queued for publish", tone: "green" },
  {
    id: "published",
    title: "Published",
    // Was "Last 7 days", which nothing enforced — the column showed every
    // piece ever published. The Board's window control now does the trimming
    // and its length is the reader's choice, so the label no longer names a
    // number it cannot keep.
    sub: "Out the door",
    tone: "neutral",
  },
];
